import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

import { api } from '../../api';
import { sentryLogError, getPlatformId } from '../../utils';
import { DEFAULT_PLATFORM_ID } from '../../constants';

import type { IFirstStepSlice, TCreateThunkApi, IFirstStepDataFromView } from './models';
import { setErrorStep, setAppIds, setStep } from './application';
import { setDraftId } from './draft';

const initialState: IFirstStepSlice = {
    abTest: undefined,
    additionalProducts: undefined,
    cardId: '',
    contractId: '',
    email: undefined,
    embossingName: '',
    firstName: '',
    lastName: '',
    middleName: '',
    mobilePhone: undefined,
    packageCashback: undefined,
    packageId: '',
    passportBirthDate: '',
    previousAppId: undefined,
    promoCode: undefined,
    refId: undefined,
    refProgram: undefined,
    sex: 'm',
    sopdConfirmed: false,
    isClient: false,
    cashbackApplyStatus: undefined,
    platformId: DEFAULT_PLATFORM_ID,
};

export const firstStepSlice = createSlice({
    name: 'firstStep',
    initialState,
    reducers: {
        setFirstStepData: (state, action: PayloadAction<IFirstStepDataFromView>) => ({
            ...action.payload,
        }),
        updateMobilePhone: (state, action: PayloadAction<IFirstStepSlice['mobilePhone']>) => {
            state.mobilePhone = action.payload;
        },
        setCashbackApplyStatus: (
            state,
            action: PayloadAction<Exclude<IFirstStepSlice['cashbackApplyStatus'], undefined>>
        ) => {
            state.cashbackApplyStatus = action.payload;
        },
        setCardId: (
            state,
            action: PayloadAction<Exclude<IFirstStepSlice['cardId'], undefined>>
        ) => {
            state.cardId = action.payload;
        },
        setUserBirthDate: (
            state,
            action: PayloadAction<Exclude<IFirstStepSlice['passportBirthDate'], undefined>>
        ) => {
            state.passportBirthDate = action.payload;
        },
        resetFirstStep: () => ({ ...initialState }),
    },
});

export const {
    resetFirstStep,
    setCardId,
    setCashbackApplyStatus,
    setFirstStepData,
    setUserBirthDate,
    updateMobilePhone,
} = firstStepSlice.actions;

/** Запрос на завершение заказа */
export const firstStepRequest = createAsyncThunk<void, IFirstStepDataFromView, TCreateThunkApi>(
    'firstStepRequest',
    async (firstStepData, { dispatch }) => {
        dispatch(setStep('checkData'));

        const { isClient, ...otherData } = firstStepData;
        const initOrderData = {
            ...otherData,
            platformId: getPlatformId(),
        };

        try {
            const { data, headers } = await api.initOrder(initOrderData);
            const { appId, draftId } = data;

            dispatch(setFirstStepData(firstStepData));
            dispatch(setUserBirthDate(initOrderData.passportBirthDate));

            dispatch(setDraftId(draftId));

            dispatch(setAppIds({ appId: appId, businessUid: headers.businessuid }));

            dispatch(setStep('smsConfirm'));
        } catch (error) {
            sentryLogError({
                error,
                message: 'applicationsInit error',
                data: firstStepData,
            });

            /* istanbul ignore if */
            if (!axios.isAxiosError(error)) {
                dispatch(setErrorStep());

                return;
            }

            dispatch(setErrorStep(error?.response?.data?.status));
        }
    }
);

/** Запрос на изменения телефона */
export const changePhoneNumberRequest = createAsyncThunk<void, number, TCreateThunkApi>(
    'changePhoneNumberRequest',
    (phoneNumber, { dispatch, getState }) => {
        const {
            application: { appId },
            firstStep,
        } = getState();

        const updatedFirstStepData = {
            ...firstStep,
            mobilePhone: phoneNumber,
            previousAppId: appId,
        };

        dispatch(firstStepRequest(updatedFirstStepData as IFirstStepDataFromView));
        dispatch(updateMobilePhone(phoneNumber));
    }
);
